/**
 * Sprint 37 — Traducción de códigos/mensajes técnicos del backend a frases
 * naturales en español para la dueña no-técnica.
 *
 * Estrategia:
 *  1. Si hay `codigo` técnico conocido, devuelve la frase mapeada.
 *  2. Si no, evalúa el mensaje: si parece humano (sin jerga ni acrónimos),
 *     se devuelve tal cual.
 *  3. Si parece técnico (Prisma errors, códigos sin traducir, stacks),
 *     devuelve frase genérica.
 *
 * Función pura, sin dependencias. Testeable.
 */

const MAPA_CODIGOS: Record<string, string> = {
  VALIDACION: 'Algunos datos no son válidos. Revisa el formulario.',
  DUPLICADO: 'Ya existe un registro con esos datos.',
  REFERENCIADO: 'No se puede borrar: hay otros datos que lo usan.',
  NO_ENCONTRADO: 'No se encontró el registro.',
  NO_AUTORIZADO: 'No tienes permisos para realizar esta acción.',
  STEP_UP_REQUIRED: 'Necesitas confirmar tu código de autenticación para continuar.',
  CANCELADO: 'Acción cancelada.',
  ERROR_INTERNO: 'Ocurrió un error inesperado. Inténtalo de nuevo.',
  IPC_REJECTED: 'No se pudo comunicar con el sistema. Reinicia la app si persiste.',
  // Prisma codes que pueden filtrarse si el handler no los mapea.
  P2002: 'Ya existe un registro con esos datos.',
  P2003: 'No se puede borrar: hay otros datos que lo usan.',
  P2025: 'No se encontró el registro.',
};

// Marcadores que indican texto técnico no apto para mostrar al usuario.
const HUELLAS_TECNICAS: RegExp[] = [
  /\b(?:Invocation of|argument|Prisma|prisma|SQLITE|SQL|stack|Error:|TypeError|ReferenceError|null|undefined)\b/,
  /^[A-Z_]{2,}(?::|$)/, // "VALIDACION:" o "ERROR_INTERNO"
  /\bat\s+\w+(?:\.\w+)*[\s(:]/, // stack trace típico ("at foo (", "at file.ts:25")
  /\\n\s+at\s/, // stack trace literal
  /^\s*\{.*\}\s*$/, // objeto JSON crudo
];

/**
 * Sprint 37 — Convierte la respuesta de error del backend en una frase
 * amigable para la dueña. Si recibe `null` o `undefined`, devuelve el
 * fallback genérico.
 *
 * @param error Mensaje literal del backend (`res.error` típicamente).
 * @param codigo Código opcional (`res.codigo`) que ipcHelpers asigna.
 */
export function traducirErrorBackend(
  error: string | null | undefined,
  codigo?: string | null,
): string {
  // 1) Mapeo por código tiene prioridad.
  if (codigo && MAPA_CODIGOS[codigo]) {
    return MAPA_CODIGOS[codigo];
  }

  // 2) Sin mensaje → fallback genérico.
  const mensaje = (error ?? '').trim();
  if (!mensaje) return MAPA_CODIGOS.ERROR_INTERNO;

  // 3) Mensajes que son obviamente técnicos → genérico.
  if (esMensajeTecnico(mensaje)) {
    return MAPA_CODIGOS.ERROR_INTERNO;
  }

  // 4) El mensaje del backend parece humano (lanzado intencionalmente por
  //    un service con texto natural). Lo devolvemos tal cual.
  return mensaje;
}

/**
 * Sprint 37 — Detecta si un texto tiene marcadores típicos de mensaje técnico
 * (no apto para mostrar al usuario final).
 */
export function esMensajeTecnico(texto: string): boolean {
  if (!texto) return false;
  if (texto.length > 300) return true; // mensajes muy largos suelen ser stacks
  return HUELLAS_TECNICAS.some((re) => re.test(texto));
}

/**
 * Sprint 37 — Helper para `IpcResult` de respuestas. Si `res.ok` es true,
 * retorna null (sin error que mostrar); si es false, traduce.
 */
export function errorDeRespuesta(
  res: { ok: true } | { ok: false; error: string; codigo?: string },
): string | null {
  if (res.ok) return null;
  return traducirErrorBackend(res.error, res.codigo);
}
